import { useEffect, useState } from 'react';
import { useMenuGate } from '../../../../../lib/menu-gate';
import { WidgetCard } from '../../WidgetCard';
import { useWidgetActions } from '../../widget-context';
import { useWidgetItems } from '../../use-widget-items';
import { useMailFolder } from '../../widget-queries';
import { mmdd } from './period';
import './widgets-v2.css';

/**
 * 메일 v2 — 표시 개수 · 기본 필터 · 메일함 · 줄에 보일 것 (2026-09-16 · 대시보드 시안 v2 V3a · 깃발 켤 때만).
 *
 * ★★★ 머리의 칩(안 읽음 · 전체 · 중요)은 **그대로 있다**(W-Q3) — 설정의 「기본 필터」는 처음 열 때 값이고,
 *     칩으로 바꾼 것은 그 자리에서만 산다(저장하지 않는다 · 규칙 D4).
 * ★★ 옛 위젯처럼 **최근 50통을 받아 화면에서 거른다** — 칩을 누를 때마다 왕복하지 않게. 받은편지함은 옛 위젯과 캐시를 나눈다.
 * ★ 빈 상태는 «전체»로 판단한다 — 거르개 때문에 카드가 통째로 사라지면 거르개를 못 되돌린다(옛 위젯과 같음).
 */

type Filter = 'unread' | 'all' | 'star';

function whenText(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  const n = new Date();
  const sameDay = d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  return sameDay
    ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    : mmdd(d);
}

export function MailWidgetV2() {
  const { go } = useWidgetActions();
  const gate = useMenuGate('mail', '메일');
  const items = useWidgetItems('mail');
  const folder = items.option<string>('folder') === 'all' ? 'all' : 'inbox';
  const { data, loading, error } = useMailFolder(folder, gate.enabled);
  const all = data ?? [];
  const unread = all.filter((m) => !m.isRead).length;

  const defaultFilter = (items.option<string>('filter') ?? 'unread') as Filter;
  const [filter, setFilter] = useState<Filter>(defaultFilter);
  // 설정을 바꾸면(설정 창 미리보기 포함) 칩도 새 기본값으로 선다.
  useEffect(() => { setFilter(defaultFilter); }, [defaultFilter]);

  const count = Number(items.option<string>('count')) || 5;
  const showSender = items.has('sender');
  const showWhen = items.has('receivedAt');
  const showPreview = items.has('preview');
  const showAttach = items.has('attachment');

  const rows = all.filter((m) => (filter === 'unread' ? !m.isRead : filter === 'star' ? m.isStarred : true));

  return (
    <WidgetCard
      title="메일"
      tail={unread > 0 ? <span className="dw-chip dw-chip--new">안읽음 {unread}</span> : null}
      link={{ label: '메일함', onClick: () => go('/mail') }}
      loading={loading}
      error={error}
      empty={all.length === 0 ? (folder === 'all' ? '메일이 없습니다' : '받은 메일이 없습니다') : null}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="wi-mail__top">
        <span className="wi-mail__cnt">{unread}<span>안 읽음</span></span>
        <span className="wi-mail__filters">
          {([['unread', '안 읽음'], ['all', '전체'], ['star', '중요']] as const).map(([k, label]) => (
            <button
              key={k} type="button"
              className={`wi-mail__f${filter === k ? ' is-on' : ''}`}
              aria-pressed={filter === k}
              onClick={() => setFilter(k)}
            >
              {label}
            </button>
          ))}
        </span>
      </div>
      <div className="dw-rows">
        {rows.length === 0 && (
          <p className="dw-note">
            {filter === 'unread' ? '안 읽은 메일이 없습니다' : filter === 'star' ? '중요 표시한 메일이 없습니다' : '받은 메일이 없습니다'}
          </p>
        )}
        {rows.slice(0, count).map((m) => {
          const when = showWhen ? whenText(m.sentAt) : null;
          const attach = showAttach && (m.attachmentCount ?? 0) > 0 ? m.attachmentCount : null;
          return (
            <button type="button" key={m.id} className="dw-row dw-row--stack" onClick={() => go('/mail')}>
              <span className={`dw-row__bullet${m.isRead ? '' : ' dw-row__bullet--on'}`} aria-hidden />
              <span className="dw-row__main">
                <span className={`dw-row__text${m.isRead ? ' dw-row__text--read' : ''}`} title={m.subject}>{m.subject}</span>
                {(showSender || attach != null) && (
                  <span className="dw-row__sub">
                    {showSender ? (m.sender?.name || m.sender?.email || '') : ''}
                    {attach != null && <span className="wv2-att">{showSender ? ' · ' : ''}첨부 {attach}</span>}
                  </span>
                )}
                {showPreview && m.bodyPreview && <span className="wv2-preview">{m.bodyPreview}</span>}
              </span>
              {when && <span className="wv2-meta">{when}</span>}
            </button>
          );
        })}
      </div>
    </WidgetCard>
  );
}
